import { makeAutoObservable } from 'mobx';
import { ItemWithLabel } from '../../../components';
import Sellable from '../../products/store/sellable/Sellable';
import Promotion, { PromotionDateState, PromotionDto } from './Promotion';

export default class Promotions {
  items: Promotion[] = [];

  stateFilter: StateFilter = 'all';

  constructor(
    dtos: PromotionDto[] = [],
    readonly sellables: Sellable[] = [],
    private readonly onSave?: (promotion: Promotion) => Promise<void>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.items = dtos.map((dto) => new Promotion(dto, this));
  }

  get filteredItems(): Promotion[] {
    if (this.stateFilter === 'all') {
      return this.items;
    }

    return this.items.filter((promotion) => {
      return promotion.dateState === this.stateFilter;
    });
  }

  get sellableOptions(): ItemWithLabel[] {
    return this.sellables.map((sellable) => ({
      label: sellable.displayName,
      value: sellable.id,
    }));
  }

  get latest(): null | Promotion {
    const sorted = [...this.items].toSorted((a, b) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      return b.updatedAt?.getTime() - a.updatedAt?.getTime();
    });

    return sorted[0];
  }

  addPromotion(): Promotion {
    const { latest } = this;

    const promotion = new Promotion(
      {
        endDate: latest?.endDate?.toISOString(),
        isEditing: true,
        price: latest?.price || 0,
        saveAction: 'create',
        startDate: latest?.startDate?.toISOString(),
      },
      this,
    );

    this.items.unshift(promotion);

    return promotion;
  }

  cancelCreating(promotion: Promotion) {
    this.items = this.items.filter((p) => p !== promotion);
  }

  async deletePromotion(promotion: Promotion) {
    this.items = this.items.filter((p) => p !== promotion);
    promotion.setSavingAction('delete');
    await this.savePromotion(promotion);
  }

  getSellableById(sellableId: string): Sellable | undefined {
    return this.sellables.find((s) => s.id === sellableId);
  }

  async savePromotion(promotion: Promotion) {
    await this.onSave?.(promotion);
  }

  setStateFilter(filter: StateFilter) {
    this.stateFilter = filter;
  }
}

export type StateFilter = 'all' | PromotionDateState;
